// 盤面左上「組合方式」紅色木牌（沿用 Unity Art/Game/pic_mageways.png）+ ways 中獎方式數字。
// 照 Unity LandscapeUI.prefab 的 WaysInfo 精確排版（1920×1080 設計座標，中心原點、y 向上）：
//   WaysInfo（牌底圖本身）aPos (−558.3, 376.2)、原尺寸 264×142；
//   WaysImage（「組合方式」字，本地化字圖 tx_mageways，zh-Hant）相對 (0, −51.9)、140×50 ＝ 牌下半；
//   WaysValueText（方式數字）相對 (0, −3)、165×50 ＝ 牌中央數字凹槽。
// 主遊戲與免費遊戲同一位置（Unity 未依模式切換）。直版照 PoriraitUI.prefab 覆寫：aPos (−446.9, 375)、scale 0.8。
//
// 數字字型：Unity WaysValueText 用位圖精靈字 tx_win1（spriteAsset=tx_win1_width.asset，金色寬版贏分字，
// glyph 高 178），**不是系統字**；fontAsset 是空 glyph 的 Empty.asset（pointSize 71、ascentLine 64.274414）、
// autoSize 關閉、fontSize 50、字距 −15、字色純白＋頂點漸層關閉（金色漸層與描邊直接烘在圖裡）。
// 故本移植改用 BitmapNumber('winWide')，字面美術直接來自圖，不用系統字＋描邊去仿（先前的作法與 Unity 不符）。

import { Container, Sprite } from 'pixi.js'
import {
  unityAnchorLandscape,
  unityAnchorPortrait,
  unityScaleLandscape,
  unityScalePortrait,
  type Orientation,
  type Resizable,
} from '../../../framework/render/design'
import type { EventBus } from '../../../framework/events/EventBus'
import type { GameEventMap } from '../../events/GameEvents'
import { getUiTexture } from '../../assets/UiTextures'
import { BitmapNumber } from '../BitmapNumber'

/** Unity WaysInfo 版面常數（橫版 LandscapeUI／直版 PoriraitUI 覆寫）。 */
const BOARD_AX = -558.3
const BOARD_AY = 376.2
const LABEL_DY = -51.9
const LABEL_W = 140
const VALUE_DY = -3
/** Unity WaysValueText m_fontSize 50、fontAsset=Empty.asset（pointSize 71、ascentLine 64.274414）。 */
const VALUE_FONT_SIZE = 50
const EMPTY_FONT_ASCENT_RATIO = 64.274414 / 71
/** 位圖字顯示高（設計 px）＝ fontSize × ascentLine/pointSize（同 WinTemp tx_multiple 的既有推導）。 */
const VALUE_HEIGHT = VALUE_FONT_SIZE * EMPTY_FONT_ASCENT_RATIO
/** 字距比例（相對顯示高）＝ TMP characterSpacing(−15) 換算：−15 × 0.01 × fontSize / VALUE_HEIGHT ≈ −0.166。 */
const VALUE_LETTER_SPACING = (-15 * 0.01 * VALUE_FONT_SIZE) / VALUE_HEIGHT
const BOARD_P_AX = -446.9
const BOARD_P_AY = 375
const BOARD_P_SCALE = 0.8

export class WaysView extends Container implements Resizable {
  private readonly board = new Sprite(getUiTexture('waysBoard'))
  /** 「組合方式」本地化字圖（Unity WaysImage 的 tx_mageways）。 */
  private readonly labelSprite = new Sprite(getUiTexture('waysLabel'))
  /** ways 數字（Unity WaysValueText 的 tx_win1 金色位圖字）。 */
  private readonly value = new BitmapNumber('winWide')

  constructor(bus: EventBus<GameEventMap>) {
    super()
    this.board.anchor.set(0.5)
    this.labelSprite.anchor.set(0.5)
    this.value.setLetterSpacing(VALUE_LETTER_SPACING)
    this.value.setText('...')
    this.addChild(this.board, this.labelSprite, this.value)

    bus.on('waysShown', ({ ways }) => {
      this.value.setText(ways.toLocaleString('en-US'))
    })
    // 開轉時先清空為 "..."，避免轉動期間停留在上一轉的組合數（對應 Unity ResetWays）。
    bus.on('waysReset', () => {
      this.value.setText('...')
    })
  }

  resize(width: number, height: number, orientation: Orientation): void {
    const isPortrait = orientation === 'portrait'
    const s = isPortrait
      ? unityScalePortrait(width, height) * BOARD_P_SCALE
      : unityScaleLandscape(width, height)
    const center = isPortrait
      ? unityAnchorPortrait(BOARD_P_AX, BOARD_P_AY, width, height)
      : unityAnchorLandscape(BOARD_AX, BOARD_AY, width, height)

    this.board.scale.set(s)
    this.board.position.set(center.x, center.y)

    // 字圖在牌下半（Unity y=−51.9）、數字在中央凹槽（y=−3）。
    this.labelSprite.scale.set((LABEL_W * s) / this.labelSprite.texture.width)
    this.labelSprite.position.set(center.x, center.y - LABEL_DY * s)
    this.value.setHeight(Math.max(13, VALUE_HEIGHT * s))
    this.value.position.set(center.x, center.y - VALUE_DY * s)
  }
}
